import React, { useMemo } from 'react';
import './StudentCountTimeline.css';
import { ClassroomRatioTimeSegment } from '../../../../server/src/types/classroom';

interface StudentCountTimelineProps {
  segments: ClassroomRatioTimeSegment[];
  timeRange: { start: Date; end: Date };
}

interface ConsolidatedSegment {
  startTime: Date;
  endTime: Date;
  studentCount: number;
}

const StudentCountTimeline: React.FC<StudentCountTimelineProps> = ({ segments, timeRange }) => {
  const totalDuration = timeRange.end.getTime() - timeRange.start.getTime();

  // Consolidate segments with the same student count
  const consolidatedSegments = useMemo(() => {
    if (!segments || segments.length === 0) return [];
    
    // Sort segments by start time
    const sortedSegments = [...segments].sort((a, b) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    
    const result: ConsolidatedSegment[] = [];
    let currentSegment: ConsolidatedSegment | null = null;
    
    sortedSegments.forEach(segment => {
      const studentCount = segment.studentIds.length;
      const segStartTime = new Date(segment.startTime);
      const segEndTime = new Date(segment.endTime);
      
      // If this is the first segment or we have a different student count
      if (!currentSegment || currentSegment.studentCount !== studentCount) {
        // Push the current segment if it exists
        if (currentSegment) {
          result.push(currentSegment);
        }
        
        // Start a new segment
        currentSegment = {
          startTime: segStartTime,
          endTime: segEndTime,
          studentCount
        };
      } else if (currentSegment.studentCount === studentCount) {
        // If the student count is the same, extend the current segment
        // But only if the segments are adjacent or overlapping
        if (segStartTime <= currentSegment.endTime) {
          currentSegment.endTime = new Date(Math.max(
            currentSegment.endTime.getTime(),
            segEndTime.getTime()
          ));
        } else {
          // If there's a gap, push the current segment and start a new one
          result.push(currentSegment);
          currentSegment = {
            startTime: segStartTime,
            endTime: segEndTime,
            studentCount
          };
        }
      }
    });
    
    // Don't forget to push the last segment
    if (currentSegment) {
      result.push(currentSegment);
    }
    
    return result;
  }, [segments]);

  return (
    <div className="student-count-timeline">
      <div className="student-count-label">
        Students
      </div>
      <div className="student-count-bars">
        {consolidatedSegments.map((segment, index) => {
          const startTime = segment.startTime.getTime();
          const endTime = segment.endTime.getTime();
          
          // Calculate position and width based on time range
          const startPos = Math.max(0, (startTime - timeRange.start.getTime()) / totalDuration * 100);
          const endPos = Math.min(100, (endTime - timeRange.start.getTime()) / totalDuration * 100);
          const width = endPos - startPos;
          
          // Skip segments that are outside our time range
          if (width <= 0) return null;
          
          return (
            <div 
              key={index}
              className="student-count-segment"
              style={{ 
                left: `${startPos}%`, 
                width: `${width}%` 
              }}
              title={`${segment.studentCount} students from ${segment.startTime.toLocaleTimeString()} to ${segment.endTime.toLocaleTimeString()}`}
            >
              <span className="student-count">{segment.studentCount}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudentCountTimeline;
